import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateJoiningInstruction from "./UpdateJoiningInstruction";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewJoiningInstructions = ({ onJoiningInstructionUpdated }) => {
  const [joiningInstructions, setJoiningInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    icon: "",
    title: "",
    subtitle: "",
    items: [{ icon: "", description: "" }]
  });

  const fetchJoiningInstructions = async () => {
    try {
      const response = await axios.get(`${apiUrl}/joininginstructions`);
      setJoiningInstructions(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load Joining Instructions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJoiningInstructions();
  }, []);

  const handleEdit = (instruction) => {
    setEditingId(instruction.id);
    setFormData({
      slug: instruction.slug,
      icon: instruction.icon,
      title: instruction.title,
      subtitle: instruction.subtitle,
      items: instruction.items.map(item => ({
        id: item.id,
        icon: item.icon,
        description: item.description
      }))
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [name]: value };
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { icon: "", description: "" }]
    }));
  };

  const removeItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(`${apiUrl}/joininginstructionsupdate/${editingId}`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (onJoiningInstructionUpdated) {
        onJoiningInstructionUpdated();
      }
      await fetchJoiningInstructions();
      setEditingId(null);
      alert("Joining Instruction updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the Joining Instruction.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Joining Instruction?")) return;
    
    try {
      await axios.delete(`${apiUrl}/joininginstructiondelete/${id}`);
      setJoiningInstructions(joiningInstructions.filter(item => item.id !== id));
      alert("Joining Instruction deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete Joining Instruction");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading Joining Instructions...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Joining Instructions</h2>
      </div>

      {joiningInstructions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No Joining Instructions found. Create your first Joining Instruction.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtitle</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {joiningInstructions.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500">{item.subtitle}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{item.items.length} steps</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingId && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateJoiningInstruction
                formData={formData}
                setFormData={setFormData}
                handleChange={handleChange}
                handleItemChange={handleItemChange}
                addItem={addItem}
                removeItem={removeItem}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingId(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewJoiningInstructions;